import {
  DELIVERY_STATUS,
  LETTER_STATUS,
  isLifetimeRecipient,
  isPosthumousRecipient,
} from "./letterSend";

function isTriggeredDelivery(status) {
  return (
    status === DELIVERY_STATUS.PENDING ||
    status === DELIVERY_STATUS.IN_PROGRESS ||
    status === DELIVERY_STATUS.RECEIVED
  );
}

/** Posthumous recipients still waiting for death / not yet triggered. */
export function getScheduledRecipients(recipients = []) {
  return recipients.filter(
    (r) =>
      isPosthumousRecipient(r) &&
      (!r.deliveryStatus || r.deliveryStatus === DELIVERY_STATUS.SCHEDULED)
  );
}

/**
 * Lifetime / immediate-send recipients, plus any posthumous already triggered.
 * Lifetime is included regardless of deliveryStatus (sendMode lifetime).
 */
export function getImmediateRecipients(recipients = []) {
  return recipients.filter(
    (r) => isLifetimeRecipient(r) || isTriggeredDelivery(r.deliveryStatus)
  );
}

/**
 * Groups letters into kanban columns.
 * Supports backend fields: letter.status, recipient.sendMode, recipient.deliveryStatus
 *
 * When a letter has both remaining scheduled posthumous recipients AND
 * lifetime/immediate recipients, it appears in both columns with filtered lists.
 */
export function categorizeLetters(letters, recipientsByLetter = {}) {
  const draft = [];
  const scheduled = [];
  const sent = [];

  letters.forEach((letter) => {
    const recipients = recipientsByLetter[letter._id] || [];

    if (recipients.length === 0) {
      draft.push({ ...letter, recipients });
      return;
    }

    const scheduledRecipients = getScheduledRecipients(recipients);
    const immediateRecipients = getImmediateRecipients(recipients);
    const hasScheduled = scheduledRecipients.length > 0;
    const hasImmediate = immediateRecipients.length > 0;

    // Split: still-scheduled posthumous + lifetime/immediate → both columns
    if (hasScheduled && hasImmediate) {
      scheduled.push({ ...letter, recipients: scheduledRecipients });
      sent.push({ ...letter, recipients: immediateRecipients });
      return;
    }

    // Death / shipping triggered, or letter already marked sent → Envoyées
    if (
      letter.status === LETTER_STATUS.SENT ||
      (hasImmediate && !hasScheduled)
    ) {
      sent.push({
        ...letter,
        recipients: hasImmediate ? immediateRecipients : recipients,
      });
      return;
    }

    // Still waiting for death / immediate send available → Programmées
    if (hasScheduled || letter.status === LETTER_STATUS.SCHEDULED) {
      scheduled.push({
        ...letter,
        recipients: hasScheduled ? scheduledRecipients : recipients,
      });
      return;
    }

    scheduled.push({ ...letter, recipients });
  });

  return { draft, scheduled, sent };
}

export function stripHtml(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

export function formatRecipientName(recipient) {
  if (!recipient) return "";
  const parts = [recipient.firstName, recipient.lastName].filter(Boolean);
  return parts.join(" ") || recipient.recipient || "";
}
